import { Subject } from 'rxjs';

export interface SharedPluginListenerOptions<T> {
  /** Register the native listeners; called once for the lifetime of the app. */
  registerListeners: (emit: (event: T) => void) => Promise<unknown>;
  /** Connect the device; called when the first consumer subscribes. */
  start: () => Promise<unknown>;
  /** Disconnect the device; called when the last consumer unsubscribes. */
  stop: () => Promise<unknown>;
  /** Prefix for console logs. */
  tag: string;
}

/**
 * Capacitor's JS bridge keeps every `addListener` callback for the life of the document, even after
 * `remove()` / `removeAllListeners()`, so registering a component closure per mount retains every destroyed
 * page or modal (DEF-14143). This registers the native listeners once, fans events out through a Subject and
 * connects the device only while at least one consumer is subscribed.
 */
export class SharedPluginListener<T> {
  private readonly events$ = new Subject<T>();
  private listenersRegistered?: Promise<unknown>;
  private consumerCount = 0;
  // Serialises start/stop so the native calls are issued in the order consumers come and go
  private queue: Promise<unknown> = Promise.resolve();

  /** Settles when the latest start attempt finished; rejects if the device failed to start. */
  ready: Promise<void> = Promise.resolve();

  constructor(private readonly options: SharedPluginListenerOptions<T>) {}

  /** Unsubscribe the returned Subscription when the consumer is destroyed. */
  subscribe(handler: (event: T) => void) {
    const subscription = this.events$.subscribe(handler);

    this.consumerCount++;
    if (this.consumerCount === 1) {
      this.ready = this.enqueue(async () => {
        await this.registerListeners();
        await this.options.start();
      });
      this.ready.catch((error) =>
        console.log(`${this.options.tag} Failed to start: `, error)
      );
    }

    subscription.add(() => {
      this.consumerCount--;
      if (this.consumerCount === 0) {
        this.enqueue(() => this.options.stop()).catch((error) =>
          // in web browser, it will throw an error.
          console.log(`${this.options.tag} Failed to stop: `, error)
        );
      }
    });

    return subscription;
  }

  private registerListeners() {
    if (!this.listenersRegistered) {
      this.listenersRegistered = this.options
        .registerListeners((event) => this.events$.next(event))
        .catch((error) => {
          // allow a retry on the next start
          this.listenersRegistered = undefined;
          throw error;
        });
    }

    return this.listenersRegistered;
  }

  private enqueue(operation: () => Promise<unknown>): Promise<void> {
    const result = this.queue.then(operation).then(() => undefined);
    this.queue = result.catch(() => undefined);
    return result;
  }
}
